/**
 * useMasterTrainers — lightweight singleton hook that fetches the
 * public list of users who have defeated all 9 NPC trainers and
 * exposes a fast `isMaster(name)` lookup.
 *
 * Used by:
 *   - ChatWidget MessageRow → ★ badge next to qualifying authors
 *   - Pal Game Leaderboard rows
 *
 * Single shared cache across the app — first mount triggers a fetch,
 * subsequent mounts reuse it. We poll once every 5 minutes so freshly
 * minted masters surface without a page refresh, but skip polling
 * when the document is hidden so background tabs don't hit the API.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let _cache = null;            // Set<string> of lowercased names
let _inflight = null;         // Promise — dedupes concurrent first-load calls
const _subscribers = new Set();
let _pollTimer = null;

async function _load() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/palgame/master-trainers")
    .then((r) => {
      _cache = new Set((r.data?.names || []).map((n) => n.toLowerCase()));
      _subscribers.forEach((fn) => fn(_cache));
      return _cache;
    })
    .catch(() => {
      _cache = new Set();
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

function _refresh() {
  if (document.hidden) return;
  api.get("/palgame/master-trainers")
    .then((r) => {
      _cache = new Set((r.data?.names || []).map((n) => n.toLowerCase()));
      _subscribers.forEach((fn) => fn(_cache));
    })
    .catch(() => {});
}

export function useMasterTrainers() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const sub = () => setVersion((v) => v + 1);
    _subscribers.add(sub);
    _load();
    if (!_pollTimer) {
      _pollTimer = setInterval(_refresh, 5 * 60 * 1000);
    }
    return () => {
      _subscribers.delete(sub);
      if (_subscribers.size === 0 && _pollTimer) {
        clearInterval(_pollTimer);
        _pollTimer = null;
      }
    };
  }, []);

  const isMaster = (name) => {
    if (!name || !_cache) return false;
    return _cache.has(String(name).toLowerCase());
  };
  return { isMaster, ready: _cache !== null, version };
}
